import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  inject,
  Injectable,
  Injector,
  OnDestroy,
} from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { filter, fromEvent } from 'rxjs';
import { MouseButton } from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { ContextMenuOption } from '../../../team-apis/content/content-connector';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { isKeyboardShortcutName } from '../keyboard-shortcuts/keyboard-shortcuts';
import { RootKeyboardShortcutService } from '../keyboard-shortcuts/root-keyboard-shortcut.service';
import { ContextMenuComponent } from './context-menu.component';

@Injectable()
export class ContextMenuService
  extends InjectableSuperclass
  implements OnDestroy
{
  private contextMenuInstance!: ContextMenuComponent;
  private componentRef!: ComponentRef<ContextMenuComponent>;
  private app = inject(ApplicationRef);
  private injector = inject(Injector);
  private keyboardShortcutService = inject(RootKeyboardShortcutService);
  private resolver = inject(ComponentFactoryResolver);

  constructor() {
    super();
    this.createContextMenuComponent();
    this.subscribeTo(
      fromEvent<MouseEvent>(document, 'mousedown', { capture: true }).pipe(
        filter((event) => event.button === MouseButton.Right),
      ),
      () => this.contextMenuInstance.closeContextMenu(),
    );
  }

  ngOnDestroy() {
    this.app.detachView(this.componentRef.hostView);
    this.componentRef.destroy();
  }

  open(
    menuOptions: ContextMenuOption[],
    clientPoint: Point2D,
    appendShortcutsToMenuOptions = false,
  ) {
    const visibleMenuOptions = (
      appendShortcutsToMenuOptions
        ? this.appendShortcutToMenuOptions(menuOptions)
        : menuOptions
    ).filter((option) => !option.hidden);
    if (!visibleMenuOptions.length) {
      return;
    }
    const position = this.calculatePosition(clientPoint);
    Object.assign(this.contextMenuInstance, {
      menuOptions: visibleMenuOptions,
      position,
    });
    this.contextMenuInstance.openContextMenu();
  }

  private createContextMenuComponent() {
    this.componentRef = this.resolver
      .resolveComponentFactory(ContextMenuComponent)
      .create(this.injector);

    this.contextMenuInstance = this.componentRef.instance;
    this.app.attachView(this.componentRef.hostView);
    document
      .querySelector('body')!
      .appendChild(this.componentRef.location.nativeElement);
  }

  private calculatePosition(clientPoint: Point2D): Point2D {
    const [x, y] = clientPoint;
    const scrollContext = document.documentElement;
    const left = x + scrollContext.scrollLeft;
    const top = y + scrollContext.scrollTop;
    return [left, top];
  }

  private appendShortcutToMenuOptions(menuOptions: ContextMenuOption[]) {
    return menuOptions.map((option) => {
      let shortcut = '';
      if (option.shortcut && isKeyboardShortcutName(option.shortcut)) {
        shortcut = this.keyboardShortcutService.humanReadableShortcut(
          option.shortcut,
        );
      }
      return { ...option, shortcut };
    });
  }
}
